import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import React, { useCallback } from 'react';
import CollaboratorsAvatarGroup from './collaborators-avatar-group';
import { type Props } from './types';
import { AccessRequestCapabilityType } from '../../state/confluence/access-request-capabilities/types';
import folderImage from './assets/cross-join-folder.svg';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import RequestAccess from './request-access';
import PendingRequest from './pending-request';
import InlineJoinProduct from './inline-join-product';
import DeniedAccess from './denied-access';
import AccessRevoked from './access-revoked';
import UnknownError from './unknown-error';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

// eslint-disable-next-line @atlaskit/design-system/use-primitives, @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const HeroImageWrapper = styled.div({
	marginBottom: token('space.200', '16px'),
});

const CrossJoinView = ({
	accessRequestCapability,
	origin,
	collaborators,
	cloudId,
	spaceKey,
	parentPageId,
	crossJoinProjectExperimentCohort,
	isCrossJoinProjectExperimentEnabled,
}: Props) => {
	// enrich analytics event with origin and then fire the event via supplied handler
	const createClickHandlerForAnalytics = useCallback(
		(eventName: string) => (_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			const originAttributes = origin?.toAnalyticsAttributes({
				hasGeneratedId: true,
			});

			fireUIAnalytics(analyticsEvent, `${eventName}Button`, {
				numberOfCollaborators: collaborators?.length,
				crossJoinProjectExperimentCohort,
				isCrossJoinProjectExperimentEnabled,
				...originAttributes,
			});
		},
		[collaborators, origin, crossJoinProjectExperimentCohort, isCrossJoinProjectExperimentEnabled],
	);

	const renderHeroImage = useCallback(
		() => (
			<HeroImageWrapper>
				{collaborators && collaborators.length > 0 ? (
					<CollaboratorsAvatarGroup collaborators={collaborators} cloudId={cloudId} />
				) : (
					<img src={folderImage} height={80} width={99.56} />
				)}
			</HeroImageWrapper>
		),
		[collaborators, cloudId],
	);

	// for scenarios where collaborators are shown (RA/join), wait until collaborators are retrieved
	if (
		accessRequestCapability &&
		[
			AccessRequestCapabilityType.REQUEST_ACCESS,
			AccessRequestCapabilityType.DIRECT_ACCESS,
		].includes(accessRequestCapability) &&
		!collaborators
	) {
		return null;
	}

	if (isCrossJoinProjectExperimentEnabled && crossJoinProjectExperimentCohort === 'variation') {
		if (
			accessRequestCapability === AccessRequestCapabilityType.REQUEST_ACCESS ||
			accessRequestCapability === AccessRequestCapabilityType.DIRECT_ACCESS
		) {
			const analyticsAction =
				accessRequestCapability === AccessRequestCapabilityType.REQUEST_ACCESS
					? 'requestAccess'
					: 'joinProduct';

			return (
				<RequestAccess
					renderHeroImage={renderHeroImage}
					onClick={createClickHandlerForAnalytics(analyticsAction)}
					origin={origin}
					spaceKey={spaceKey}
					parentPageId={parentPageId}
					crossJoinProjectExperimentCohort={crossJoinProjectExperimentCohort}
					accessRequestCapability={accessRequestCapability}
				/>
			);
		}
	}

	switch (accessRequestCapability) {
		case AccessRequestCapabilityType.REQUEST_ACCESS:
			return (
				<RequestAccess
					renderHeroImage={renderHeroImage}
					onClick={createClickHandlerForAnalytics('requestAccess')}
					origin={origin}
					spaceKey={spaceKey}
					parentPageId={parentPageId}
				/>
			);

		case AccessRequestCapabilityType.PENDING_REQUEST_EXISTS:
			return <PendingRequest crossJoinProjectExperimentCohort={crossJoinProjectExperimentCohort} />;

		case AccessRequestCapabilityType.DENIED_REQUEST_EXISTS:
			return <DeniedAccess />;

		case AccessRequestCapabilityType.APPROVED_REQUEST_EXISTS:
			return <AccessRevoked />;

		case AccessRequestCapabilityType.DIRECT_ACCESS:
			return (
				<InlineJoinProduct
					onClick={createClickHandlerForAnalytics('joinProduct')}
					renderHeroImage={renderHeroImage}
				/>
			);
		default:
			return <UnknownError />;
	}
};

export default CrossJoinView;
